<template>
  <div>
    <el-dropdown
      v-if="otherAccounts.length"
      class="account-switcher right-menu-item hover-effect"
      trigger="click"
      placement="bottom-start"
    >
      <div class="account-switcher__trigger">
        <span>切換帳號</span>
        <i class="el-icon-arrow-down" style="margin-left: 4px;" />
      </div>
      <el-dropdown-menu slot="dropdown">
        <el-dropdown-item
          v-for="(item, index) in otherAccounts"
          :key="index"
          :disabled="switching"
          class="account-switcher__item-wrap"
          @click.native="handleSwitch(item)"
        >
          <div class="account-switcher__item">
            <div class="account-switcher__info">
              <span class="account-switcher__name">{{ item.user.name }}</span>
              <span class="account-switcher__email">{{ item.user.AuthEmail && item.user.AuthEmail.email }}</span>
            </div>
            <span
              class="account-switcher__remove"
              @click.stop="handleRemove(item, $event)"
            >移除</span>
          </div>
        </el-dropdown-item>
      </el-dropdown-menu>
    </el-dropdown>
    <DeleteDialog
      v-if="showDeleteDialog"
      content="確定要移除此帳號嗎？"
      @close="showDeleteDialog = false"
      @delete="confirmDelete"
    />
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import { useRouter } from 'vue-router/composables'
import store from '@/store'
import ls from '@/utils/localstorage'
import { EmailLogin } from '@/api/auth'
import { resetRouter } from '@/router'
import { useRecaptcha } from '@/use/recaptcha'

export default defineComponent({
  name: 'HeadbarAccountSwitcher',
  setup () {
    const router = useRouter()
    const switching = ref(false)
    const {
      recaptchaVerifyToken,
      v3RecaptchaVerify,
      recaptchaVersionFailed,
      recaptchaVersionUse,
    } = useRecaptcha()

    const currentUser = computed(() => store.getters.user)

    const otherAccounts = computed(() => {
      const all = ls.getStoreAccount() || []
      if (!currentUser.value) return all
      const currentKey = `${currentUser.value.OrgId}:${currentUser.value.id}`
      return all.filter(
        (item) => `${item.user.OrgId}:${item.user.id}` !== currentKey,
      )
    })

    const handleSwitch = async (item) => {
      if (switching.value) return
      if (!item.credentials) {
        window.$message.warning('此帳號尚未儲存登入資訊，請重新手動登入一次以啟用快速切換')
        return
      }

      switching.value = true
      try {
        if (!recaptchaVersionFailed.v3) {
          await v3RecaptchaVerify('loginByEmail')
        }

        const payload = {
          ...item.credentials,
          agreeTerms: true,
          recaptchaToken: recaptchaVerifyToken.value,
          recaptchaVersion: recaptchaVersionUse.value,
        }

        const [res, err] = await EmailLogin(payload)
        if (err) {
          window.$message.error(err)
          return
        }

        if (res.token) {
          resetRouter()
          store.commit('user/SET_ROLES', '')
          store.commit('user/SET_USER', null)
          store.commit('user/SET_NOW_SHOP', null)
          store.commit('app/SET_SIDEBAR_PAGE', 1)
          store.commit('auth/SET_TOKEN', res.token)
          window.$message.success(`已切換至 ${item.user.name}`)
          router.push('/').catch(() => {})
        }
      } finally {
        switching.value = false
      }
    }

    const deleteTarget = ref(null)
    const showDeleteDialog = ref(false)

    const handleRemove = (item, event) => {
      event.stopPropagation()
      deleteTarget.value = item
      showDeleteDialog.value = true
    }

    const confirmDelete = () => {
      if (!deleteTarget.value) return
      store.dispatch('user/deleteAccount', {
        orgId: deleteTarget.value.user.OrgId,
        userId: deleteTarget.value.user.id,
      })
      showDeleteDialog.value = false
      deleteTarget.value = null
    }

    return {
      otherAccounts,
      switching,
      handleSwitch,
      handleRemove,
      showDeleteDialog,
      confirmDelete,
    }
  },
})
</script>

<style lang="scss" scoped>
.account-switcher {
  &__trigger {
    @apply flex items-center cursor-pointer text-[16px];
    color: #2d271b;
    font-weight: 400;
  }

  &__item-wrap {
    padding: 0 !important;
  }

  &__item {
    @apply flex items-center justify-between;
    min-width: 220px;
    padding: 8px 16px;
  }

  &__info {
    @apply flex flex-col gap-[2px] min-w-0 mr-[16px];
  }

  &__name {
    @apply text-[14px] font-medium truncate;
    color: #303133;
  }

  &__email {
    @apply text-[12px] truncate;
    color: #909399;
  }

  &__remove {
    @apply text-[12px] flex-shrink-0 cursor-pointer;
    color: #c0c4cc;

    &:hover {
      color: var(--danger, #f56c6c);
    }
  }
}
</style>
